import { useMemo } from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { Status } from "src/types/authData";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import useUser from "./useUser";

export function useFormTemplateSettings(formTemplateId: string) {
  const user = useUser();
  const role = user.status === Status.OK ? user.data.role : null;

  const formTemplateSettingsForAdmin = useFormTemplateSettingsForAdmin(
    formTemplateId,
    role !== HasuraRole.USER
  );
  const formTemplateSettingsForGuardian = useFormTemplateSettingsForGuardian(
    formTemplateId,
    role === HasuraRole.USER
  );

  if (role === HasuraRole.USER) {
    return formTemplateSettingsForGuardian;
  } else {
    return formTemplateSettingsForAdmin;
  }
}

function useFormTemplateSettingsForAdmin(
  formTemplateId: string,
  enabled: boolean
) {
  const formTemplateSettingsQuery = FSQ.useGetFormTemplateSettings(
    {
      path: {
        formTemplateId,
      },
    },
    [formTemplateId],
    {
      enabled: !!formTemplateId && enabled,
    }
  );

  const formTemplateSettingsValue = useMemo(
    () =>
      RD.fromRestResult(formTemplateSettingsQuery)
        .map((rd) => rd.data)
        .mapError(
          (error) =>
            new Error("Error retrieving form template settings", {
              cause: error,
            })
        ),
    // formTemplateSettingsQuery is not a dependency because it is not a stable object
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formTemplateSettingsQuery.data,
      formTemplateSettingsQuery.isError,
      formTemplateSettingsQuery.isLoading,
      formTemplateSettingsQuery.isRefetching,
      formTemplateSettingsQuery.error,
    ]
  );

  return formTemplateSettingsValue;
}

function useFormTemplateSettingsForGuardian(
  formTemplateId: string,
  enabled: boolean
) {
  const formTemplateSettingsQuery = FSQ.useGetFormTemplateSettingsForMe(
    {
      path: {
        formTemplateId,
      },
    },
    [formTemplateId],
    {
      enabled: !!formTemplateId && enabled,
    }
  );

  const formTemplateSettingsValue = useMemo(
    () =>
      RD.fromRestResult(formTemplateSettingsQuery)
        .map((rd) => rd.data)
        .mapError(
          (error) =>
            new Error("Error retrieving form template settings", {
              cause: error,
            })
        ),
    // formTemplateSettingsQuery is not a dependency because it is not a stable object
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formTemplateSettingsQuery.data,
      formTemplateSettingsQuery.isError,
      formTemplateSettingsQuery.isLoading,
      formTemplateSettingsQuery.isRefetching,
      formTemplateSettingsQuery.error,
    ]
  );

  return formTemplateSettingsValue;
}
